import { X, Check } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormInput } from "@/components/forms";
import { FormComboboxForeignKey } from "../../../components/form-combobox-foreign-key";
import { REFERENCE_TABLE } from "@/hooks/use-foreign-key-data";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";

const schema = z.object({
  processName: z.string({ required_error: "Process Name is required" }).min(1),
  shopId: z.coerce.number({ required_error: "Shop is required" }),
});

interface AddProcessSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any, form: any) => void;
  isLoading?: boolean;
}

export function AddProcessSheet({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: AddProcessSheetProps) {
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      processName: "",
      shopId: undefined,
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    form.reset();
  };

  const handleSubmit = (data: any) => {
    onSubmit(data, form);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="aero:sm:max-w-[600px] aero:p-0 aero:gap-0 aero:flex aero:flex-col"
      >
        <style>{`
          [data-slot="sheet-content"] > button[class*="absolute"][class*="right-4"] {
            display: none;
          }
        `}</style>
        <SheetHeader className="aero:p-6 aero:pb-4 aero:gap-3 aero:border-b aero:border-neutral-200">
          <div className="aero:flex aero:items-center aero:justify-between aero:h-12 aero:gap-3">
            <SheetTitle className="aero:font-bold aero:text-xl aero:leading-9 aero:text-foreground">
              Add New Process
            </SheetTitle>
            <SheetClose asChild>
              <button
                onClick={handleClose}
                className="aero:w-6 aero:h-6 aero:flex aero:items-center aero:justify-center aero:rounded aero:hover:bg-neutral-100"
              >
                <X className="aero:w-5 aero:h-5 aero:text-neutral-500" />
              </button>
            </SheetClose>
          </div>
        </SheetHeader>
        <div className="aero:flex-1 aero:overflow-y-auto aero:px-6 aero:py-5">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="aero:flex aero:flex-col aero:gap-5"
            >
              <FormInput
                name="processName"
                label="Process Name"
                placeholder="Enter process name"
                control={form.control}
                type="text"
                required
              />
              <FormComboboxForeignKey
                name="shopId"
                label="Shop"
                referenceTable={REFERENCE_TABLE.ppsShop}
                control={form.control}
                nestedColumnName={""}
                required
              />
            </form>
          </Form>
        </div>
        <SheetFooter className="aero:p-6 aero:pt-4 aero:border-t aero:border-neutral-200 aero:flex aero:flex-row aero:justify-end aero:gap-3">
          <Button
            type="button"
            intent="neutral"
            design="outline"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            intent="primary"
            onClick={form.handleSubmit(handleSubmit)}
            disabled={isLoading}
            className="aero:h-10 aero:gap-2 aero:px-4 aero:bg-[#2F4F4F] hover:aero:bg-[#1F3F3F]"
          >
            {isLoading ? (
              <>
                <span className="aero:animate-spin aero:w-4 aero:h-4 aero:border-2 aero:border-white aero:border-t-transparent aero:rounded-full" />
                Creating...
              </>
            ) : (
              <>
                <Check className="aero:w-5 aero:h-5" />
                Create Process
              </>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
